import { useCallback, useEffect, useState } from "react";
import { Languages, Check, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";

const errorMessage = (e) => typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Operazione non riuscita. Riprova.";

export default function AdminTranslations() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/translations");
      setRows(data);
      setDrafts(Object.fromEntries(data.map(r => [r.key, r.en])));
      setLoadError("");
    } catch { setLoadError("Impossibile caricare le traduzioni. Riprova."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAi = async () => {
    setBusy(true);
    try {
      await api.post("/admin/translations/run");
      toast.success("Traduzione avviata: resta su questa pagina.");
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/admin/translations/status");
          setProgress(data.translated || 0);
          if (!data.running) {
            clearInterval(poll);
            setBusy(false);
            setProgress(0);
            if (data.error) toast.error(data.error); else toast.success(data.message || "Traduzione completata");
            await load();
          }
        } catch { clearInterval(poll); setBusy(false); setProgress(0); }
      }, 3000);
    } catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };

  const saveOne = async (key) => {
    setBusy(true);
    try {
      await api.put("/admin/translations", { key, en: drafts[key] || "" });
      toast.success("Traduzione salvata");
      await load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="space-y-4" data-testid="admin-translations-loading"><Skeleton className="h-20" />{[0, 1, 2].map(i => <Skeleton className="h-24" key={i} />)}</div>;
  if (loadError) return <div className="menu-state" role="alert" data-testid="admin-translations-error"><h2>Caricamento non riuscito</h2><p>{loadError}</p><Button onClick={() => { setLoading(true); load(); }} data-testid="admin-translations-retry"><RefreshCw /> Riprova</Button></div>;

  const pending = rows.filter(r => !r.en || r.stale).length;
  return <div data-testid="admin-translations">
    <div className="admin-subcategories-intro">
      <div>
        <h3 data-testid="admin-translations-heading">Traduzioni in inglese</h3>
        <p data-testid="admin-translations-description">I nomi dei piatti restano sempre in italiano. Qui traduci descrizioni, categorie, sottocategorie e note. Puoi correggere ogni testo a mano.</p>
      </div>
      <div className="flex items-center gap-3">
        <Badge variant="outline" className="bg-card" data-testid="admin-translations-count">{rows.length - pending}/{rows.length} tradotti</Badge>
        <Button onClick={runAi} disabled={busy || !pending} data-testid="admin-translations-run"><Languages />{busy ? `Traduzione… ${progress}` : pending ? `Traduci con AI (${pending})` : "Tutto tradotto"}</Button>
      </div>
    </div>
    {rows.length ? <div className="admin-translation-list">
      {rows.map(row => <Card key={row.key} className="admin-translation-card" data-testid={`translation-${row.key}`}>
        <div className="admin-translation-head">
          <span className="admin-translation-label" data-testid={`translation-label-${row.key}`}>{row.label}</span>
          {row.stale ? <Badge variant="secondary" data-testid={`translation-stale-${row.key}`}>Testo italiano modificato</Badge> : !row.en ? <Badge variant="secondary" data-testid={`translation-missing-${row.key}`}>Da tradurre</Badge> : null}
        </div>
        <p className="admin-translation-source" data-testid={`translation-source-${row.key}`}>{row.source}</p>
        <Textarea value={drafts[row.key] ?? ""} onChange={e => setDrafts({ ...drafts, [row.key]: e.target.value })} placeholder="Traduzione in inglese…" aria-label={`Traduzione inglese di ${row.label}`} data-testid={`translation-input-${row.key}`} />
        <Button size="sm" variant="outline" className="justify-self-start" disabled={busy || (drafts[row.key] ?? "") === row.en} onClick={() => saveOne(row.key)} data-testid={`translation-save-${row.key}`}><Check /> Salva</Button>
      </Card>)}
    </div> : <div className="menu-state" data-testid="admin-translations-empty"><h3>Nessun testo da tradurre</h3><p>Aggiungi descrizioni, categorie o note al menu per vederle qui.</p></div>}
  </div>;
}
